import { ActiveServicePreview, ServiceContainer } from '@shared/components';
import { OnboardingSaveParkingDto, DayOfWeek } from '@interfaces/models';
import { CityDto, ZoneDto } from '@interfaces/types';
import { Parking } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { Separator } from '@ui/components';
import { useEffect, FC } from 'react';

import { ParkingConfigurationForm } from './parking-configuration-form/parking-configuration-form';
import { useParkingConfiguration } from './use-parking-configuration';

interface ParkingConfigurationProps {
  onConfigurationChange: (config: OnboardingSaveParkingDto | null) => void;
  setIsEditing?: (isEditing: boolean) => void;
  initialConfiguration?: OnboardingSaveParkingDto | null;
  defaultSelected?: boolean;
  toggleDisabled?: boolean;
  canNotEdit?: boolean;
  className?: string;
  days: DayOfWeek[];
  cities: CityDto[];
  zones: ZoneDto[];
}

export const ParkingConfiguration: FC<ParkingConfigurationProps> = ({
  onConfigurationChange,
  initialConfiguration,
  defaultSelected,
  toggleDisabled,
  setIsEditing,
  canNotEdit,
  className,
  cities,
  zones,
  days,
}) => {
  const { t } = useTranslation();

  const {
    createDefaultConfiguration,
    prepareParkingPreviewData,
    handleRevertChanges,
    handleActiveChange,
    isEntityActive,
    configuration,
    handleEdit,
    handleSave,
    isEditing,
  } = useParkingConfiguration({
    onUpdate: onConfigurationChange,
    initialConfiguration,
    defaultSelected,
    cities,
    zones,
    days,
  });

  useEffect(() => setIsEditing?.(isEditing), [isEditing, setIsEditing]);

  const defaultParkingConfiguration = createDefaultConfiguration();

  const parkingConfigurationPreviewData = prepareParkingPreviewData(
    configuration ?? defaultParkingConfiguration,
  );

  return (
    <ServiceContainer
      title={t('COMPANY_POLICY.PARKING_CONFIGURATION_TITLE')}
      previewData={parkingConfigurationPreviewData}
      onActiveChange={handleActiveChange}
      toggleDisabled={toggleDisabled}
      active={isEntityActive}
      className={className}
      icon={<Parking />}
    >
      <>
        {(isEditing || isEntityActive) && <Separator className="mt-4 mb-4" />}

        <div>
          {isEntityActive && !isEditing && (
            <ActiveServicePreview
              data={parkingConfigurationPreviewData}
              canNotEdit={canNotEdit}
              onEdit={handleEdit}
            />
          )}

          {isEditing && (
            <div>
              <ParkingConfigurationForm
                defaultParkingConfiguration={configuration ?? defaultParkingConfiguration}
                onRevertChanges={handleRevertChanges}
                onSubmit={handleSave}
                cities={cities}
                zones={zones}
                days={days}
              />
            </div>
          )}
        </div>
      </>
    </ServiceContainer>
  );
};
